import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { normalizeScannedCode } from "../util/barcode";
import { isEmpty } from "lodash";
import { diff } from "deep-object-diff";

/**
 * Unit type categories for scanned items.
 * Maps to ITEM_LOCATION_ITEM_TYPES values in ItemLocation.js.
 */
export const RECEIVING_ITEM_TYPES = {
  unit: { label: "Item / Material", icon: "window" },
  screen: { label: "Screen", icon: "screens" },
  hardware: { label: "Hardware", icon: "hardware" },
  accessory: { label: "Accessory", icon: "accessory" },
};
deepFreeze(RECEIVING_ITEM_TYPES);

export const SELECTED_ORDER_FIELDS = {
  projectID: "",
  saleID: "",
  orderID: "",
  vendor: "",
  orderPO: "",
  ackID: null,
  source: "manual", // 'vendor' | 'manual'
  location: null, // warehouse location (auto-fills after first scan for this PO)
  packingSlipDocument: null, // FileRef for uploaded packing slip (non-vendor-tracked)
  missingDocument: false,
  packingSlipID: null, // set on completion
};
deepFreeze(SELECTED_ORDER_FIELDS);

export const RECEIVING_SESSION_FIELDS = {
  status: "setup", // 'setup' | 'scanning' | 'completed' | 'canceled'
  // Set the first time a scan lands and never unset. A canceled session whose
  // scans were reverted has none left, so this is the only thing that tells it
  // apart from one where nothing ever happened — and only the latter is worth
  // deleting outright.
  hadScans: false,
  selectedOrders: [],
  // The truck being unloaded, learned from the first package scanned. Once
  // set, every order on that truck is expected and a package from another
  // truck is refused. Null until a scan names one, and never Marvin's "0"
  // placeholder for a load not yet on a truck.
  truckNumber: null,
  vendor: "", // The truck's vendor, for the header
  // Who is leading this session right now: the only one (besides an admin)
  // with the Cancel / Pause / Finish controls. Everyone else on the truck is
  // helping and can only Exit. It starts as whoever pressed Start and moves to
  // whoever Resumes a paused truck -- so a lead who leaves mid-truck (or whose
  // device dies) does not lock it. Null means nobody is leading (paused, or
  // the lead stepped into the Complete step): the other devices Exit
  // themselves, and the next Resume claims it -- unless `finishingBy` says
  // the truck is being finished.
  activeOwner: null,
  // Who tapped Finish, and when: set on the way into the Complete step and
  // cleared by Back (or a confirmed Take over). Without it a Finish looked
  // exactly like a Pause, so helpers were told to rejoin from Recent trucks,
  // and their Resume took the lead while the lead was saving the packing
  // slips. While it is set (and nobody leads), helpers are told the truck is
  // being finished, and Recent trucks offers only its finisher a Resume --
  // anyone else gets a confirmed Take over.
  finishingBy: null,
  finishingAt: null,
  // The completed session this one continues, when a truck marked done too
  // soon is reopened. Scans are not copied: the vendor records already say
  // what the first pass received.
  resumedFrom: null,
  // orderID -> id of the packing slip (or unassigned record) Complete saved
  // for it. Complete saves each PO in turn and stops short of finishing the
  // truck when one fails, so a retry -- from this device or after a reload --
  // skips what is already saved instead of saving it twice.
  savedPackingSlips: {},

  // Meta info
  startedAt: null,
  startedBy: null,
  completedAt: null,
  completedBy: null,
  canceledAt: null,
  canceledBy: null,
};
deepFreeze(RECEIVING_SESSION_FIELDS);

/**
 * Individual scan record — stored as subcollection:
 * receivingSessions/{sessionID}/scans/{scanID}
 */
export const SCAN_FIELDS = {
  barcode: "",
  scannedAt: null,
  scannedBy: null,

  // PO assignment
  projectID: "",
  saleID: "",
  orderID: "",

  // Item classification
  itemType: "unit", // 'unit' | 'screen' | 'hardware' | 'accessory'
  location: null, // warehouse:zone or warehouse:zone:bin
  description: "", // from vendor data or manual entry

  // Vendor match data (null if unmatched)
  matched: false,
  vendorShipmentID: null,
  vendorLineIndex: null,
  vendorTrackingIndex: null,
  vendorCharacterCode: null,

  // Label-read match data. A barcode resolves a PACKAGE; a photographed label
  // resolves the SALE ITEM directly, so the item is recorded rather than
  // re-derived. `component` is set only for a carton that is part of an item --
  // one panel of a multi-slide door -- and is what completeReceiving folds so
  // five cartons credit one door instead of five.
  //
  // Both are declared here because sanitize() picks only the keys named in this
  // map: an undeclared field is dropped on the way to Firestore, silently.
  saleItemID: null,
  component: null,

  // Issue tracking
  hasIssue: false,
  issueID: null,
};
deepFreeze(SCAN_FIELDS);

export default class ReceivingSession extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, RECEIVING_SESSION_FIELDS);

    // Convert to Date
    data.startedAt = timestampToDate(data.startedAt);
    data.completedAt = timestampToDate(data.completedAt);
    data.canceledAt = timestampToDate(data.canceledAt);
    data.finishingAt = timestampToDate(data.finishingAt);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
    });
  }

  // Permissions

  canBeEditedBy(user) {
    return user.isEmployee;
  }

  // Database

  get path() {
    return ReceivingSession.getPath(this.id);
  }

  static getPath(id) {
    let path = "receivingSessions";
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static getScanPath(sessionID, scanID) {
    let path = `receivingSessions/${sessionID}/scans`;
    if (scanID) {
      path += `/${scanID}`;
    }
    return path;
  }

  static toFirestore(session) {
    return session.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new ReceivingSession(snapshot.id, data);
  }

  // Computed

  get isSetup() {
    return this.data.status === "setup";
  }

  get isScanning() {
    return this.data.status === "scanning";
  }

  /**
   * Still open, but its lead is on the Complete step saving it. Only while
   * nobody leads: a device that leads it (an older app's Resume, which does
   * not clear `finishingBy`) has it back in scanning.
   */
  get isFinishing() {
    return (
      this.isScanning &&
      Boolean(this.data.finishingBy) &&
      this.data.activeOwner == null
    );
  }

  get isCompleted() {
    return this.data.status === "completed";
  }

  get isCanceled() {
    return this.data.status === "canceled";
  }

  get startedByUser() {
    return this.data.startedBy
      ? store.state.main.users[this.data.startedBy]
      : null;
  }

  get completedByUser() {
    return this.data.completedBy
      ? store.state.main.users[this.data.completedBy]
      : null;
  }

  /**
   * Get this session's scans for a specific order from the store, which may
   * hold other sessions' scans too (each carries its `sessionID`).
   */
  scansForOrder(projectID, saleID, orderID) {
    return Object.values(store.state.main.receivingScans).filter(
      (scan) =>
        scan.sessionID === this.id &&
        scan.projectID === projectID &&
        scan.saleID === saleID &&
        scan.orderID === orderID
    );
  }

  /**
   * Check if a barcode has already been scanned in this session.
   */
  isBarcodeScanned(barcode) {
    return Object.values(store.state.main.receivingScans).some(
      (scan) => scan.sessionID === this.id && scan.barcode === barcode
    );
  }

  /**
   * Strip scanner prefix/suffix artefacts and shifted digits from a scanned
   * code. See src/util/barcode.js for what the dock's scanners actually send.
   */
  static normalizeBarcode(code) {
    return normalizeScannedCode(code);
  }

  /**
   * Map a vendor CharacterCode to our item types.
   */
  static characterCodeToItemType(characterCode) {
    if (!characterCode) return "unit";
    switch (characterCode.toUpperCase()) {
      case "SC":
        return "screen";
      case "SH":
      case "GR":
      case "CSG":
      case "ST":
        return "hardware";
      case "BN":
        return "accessory";
      default:
        return "unit";
    }
  }

  isEqual(session) {
    return this.id === session.id && isEmpty(diff(this.data, session.data));
  }
}
